import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Users, Search, Loader2, User, Phone, Mail, Briefcase, Building, Calendar, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import InfoBanner from '../../components/ui/info-banner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BranchEmployees = ({ branchIdOverride, readOnly = false }) => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('fortis'); // 'fortis' veya taşeron id
  
  // Sıralama state'leri
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Yeni çalışan sayaçları (şirket bazında) ve yeni çalışan id'leri
  const [newCounts, setNewCounts] = useState({});
  const [newEmployeeIds, setNewEmployeeIds] = useState(new Set());

  const getHeaders = () => {
    const token = localStorage.getItem('fortis_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadNewCounts = async () => {
    if (readOnly) return;
    try {
      const params = branchIdOverride ? { branch_id: branchIdOverride } : {};
      const res = await axios.get(`${API}/branch/employees/new-counts`, { headers: getHeaders(), params });
      setNewCounts(res.data?.counts || {});
      setNewEmployeeIds(new Set(res.data?.new_employee_ids || []));
    } catch (e) {
      console.error('Yeni çalışan sayıları yüklenemedi:', e);
    }
  };

  const markCompanySeen = async (companyKey) => {
    if (readOnly) return;
    setNewCounts(prev => ({ ...prev, [companyKey]: 0 }));
    try {
      await axios.post(`${API}/branch/employees/mark-seen`,
        { company_key: companyKey, ...(branchIdOverride ? { branch_id: branchIdOverride } : {}) },
        { headers: getHeaders() });
    } catch (e) {
      console.error('Görüldü işaretlenemedi:', e);
    }
  };

  // Açılan şirket sekmesini görüldü olarak işaretle (sayaç temizlenir, satırlardaki "Yeni" rozeti bu oturumda kalır)
  useEffect(() => {
    if (!loading && !readOnly) {
      markCompanySeen(selectedCompany);
    }
  }, [selectedCompany, loading]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Çalışanları yükle
      const empRes = await axios.get(`${API}/branch/employees`, { headers });
      setEmployees(empRes.data || []);
      
      // Bu şubedeki taşeronları yükle (çalışanı olan taşeronlar)
      try {
        const subRes = await axios.get(`${API}/branch/subcontractors`, { headers });
        setSubcontractors(subRes.data || []);
      } catch (e) {
        console.error('Taşeronlar yüklenemedi:', e);
      }
      loadNewCounts();
    } catch (error) {
      console.error('Error loading employees:', error);
      toast.error('Çalışanlar yüklenirken hata oluştu');
    }
    setLoading(false);
  };

  // Sıralama handler
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sıralama ikonu
  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="ml-1 h-4 w-4 text-gray-400 inline" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="ml-1 h-4 w-4 text-amber-600 inline" />
      : <ArrowDown className="ml-1 h-4 w-4 text-amber-600 inline" />;
  };

  // Şirkete göre filtrele
  const getFilteredByCompany = () => {
    if (selectedCompany === 'fortis') {
      return employees.filter(emp => !emp.subcontractor_id);
    }
    return employees.filter(emp => emp.subcontractor_id === selectedCompany);
  };

  // Arama filtresini uygula
  const filteredEmployees = getFilteredByCompany().filter(emp => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (emp.name && emp.name.toLowerCase().includes(search)) ||
      (emp.tc_no && emp.tc_no.includes(search)) ||
      (emp.position && emp.position.toLowerCase().includes(search)) ||
      (emp.phone && emp.phone.includes(search))
    );
  });

  // Sıralanmış çalışanlar
  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    if (!sortColumn) return 0;
    
    let valA, valB;
    
    switch (sortColumn) {
      case 'name':
        valA = a.name || '';
        valB = b.name || '';
        break;
      case 'profession':
        valA = a.profession || '';
        valB = b.profession || '';
        break;
      case 'position':
        valA = a.position || a.position_name || '';
        valB = b.position || b.position_name || '';
        break;
      case 'phone':
        valA = a.phone || '';
        valB = b.phone || '';
        break;
      case 'start_date':
        valA = a.start_date || a.hire_date || '';
        valB = b.start_date || b.hire_date || '';
        // Tarih karşılaştırması
        if (valA && valB) {
          return sortDirection === 'asc' 
            ? new Date(valA) - new Date(valB)
            : new Date(valB) - new Date(valA);
        }
        if (!valA) return sortDirection === 'asc' ? 1 : -1;
        if (!valB) return sortDirection === 'asc' ? -1 : 1;
        return 0;
      case 'status':
        valA = a.employment_status || '';
        valB = b.employment_status || '';
        break;
      default:
        return 0;
    }
    
    if (typeof valA === 'string' && typeof valB === 'string') {
      return sortDirection === 'asc' 
        ? valA.localeCompare(valB, 'tr') 
        : valB.localeCompare(valA, 'tr');
    }
    return 0;
  });

  // Şirket bazlı çalışan sayısı
  const getCompanyCount = (companyId) => {
    if (companyId === 'fortis') {
      return employees.filter(emp => !emp.subcontractor_id).length;
    }
    return employees.filter(emp => emp.subcontractor_id === companyId).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="branch-employees">
      {/* Bilgilendirme */}
      <InfoBanner id="branch-employees-info" variant="info">
        <strong>Şube Çalışanları:</strong> Şube Çalışanları bölümünde Fortis ve diğer Taşeronların şubenizde görevlendirdiği kişilerin listesini görebilirsiniz.
      </InfoBanner>

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Şube Çalışanları</h1>
          <p className="text-gray-500">
            {user?.branch_name} şubesine bağlı çalışanlar ({employees.length} kişi)
          </p>
        </div>
      </div>

      {/* Şirket Tab'ları */}
      <div className="flex flex-wrap gap-2">
        {/* FORTİS Tab */}
        <Button
          variant={selectedCompany === 'fortis' ? 'default' : 'outline'}
          className={`relative ${selectedCompany === 'fortis' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
          onClick={() => setSelectedCompany('fortis')}
          data-testid="company-tab-fortis"
        >
          <Building className="h-4 w-4 mr-2" />
          FORTİS
          <Badge variant="secondary" className="ml-2 bg-white/20">
            {getCompanyCount('fortis')}
          </Badge>
          {newCounts['fortis'] > 0 && (
            <span
              className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center shadow"
              data-testid="new-count-fortis"
              title={`${newCounts['fortis']} yeni çalışan`}
            >
              {newCounts['fortis']}
            </span>
          )}
        </Button>
        
        {/* Taşeron Tab'ları */}
        {subcontractors.map(sub => (
          <Button
            key={sub.id}
            variant={selectedCompany === sub.id ? 'default' : 'outline'}
            className={`relative ${selectedCompany === sub.id ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
            onClick={() => setSelectedCompany(sub.id)}
            data-testid={`company-tab-${sub.id}`}
          >
            <Users className="h-4 w-4 mr-2" />
            {sub.name}
            <Badge variant="secondary" className="ml-2 bg-white/20">
              {getCompanyCount(sub.id)}
            </Badge>
            {newCounts[sub.id] > 0 && (
              <span
                className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center shadow"
                data-testid={`new-count-${sub.id}`}
                title={`${newCounts[sub.id]} yeni çalışan`}
              >
                {newCounts[sub.id]}
              </span>
            )}
          </Button>
        ))}
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input 
            placeholder="Çalışan ara (ad, TC, pozisyon, telefon)..." 
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {employees.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Users className="h-16 w-16 mx-auto mb-4 text-gray-200" />
            <h3 className="text-xl font-bold mb-2">Çalışan Bulunmuyor</h3>
            <p className="text-gray-500 mb-4">
              {user?.branch_name} şubesinde henüz çalışan kaydı yok
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-16 text-center font-bold">No</TableHead>
                  <TableHead 
                    className="font-bold cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('name')}
                  >
                    Ad Soyad
                    <SortIcon column="name" />
                  </TableHead>
                  <TableHead 
                    className="font-bold cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('profession')}
                  >
                    Meslek
                    <SortIcon column="profession" />
                  </TableHead>
                  <TableHead 
                    className="font-bold cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('position')}
                  >
                    Görev
                    <SortIcon column="position" />
                  </TableHead>
                  <TableHead 
                    className="font-bold cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('phone')}
                  >
                    Telefon
                    <SortIcon column="phone" />
                  </TableHead>
                  <TableHead 
                    className="font-bold cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('start_date')}
                  >
                    İşe Giriş Tarihi
                    <SortIcon column="start_date" />
                  </TableHead>
                  <TableHead 
                    className="font-bold cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('status')}
                  >
                    Durum
                    <SortIcon column="status" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedEmployees.map((emp, index) => (
                  <TableRow key={emp.id}>
                    <TableCell className="text-center font-bold text-gray-500">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                          <User className="h-4 w-4 text-amber-600" />
                        </div>
                        <span className="font-medium">{emp.name}</span>
                        {newEmployeeIds.has(emp.id) && (
                          <Badge className="bg-red-600 text-white text-xs" data-testid={`new-employee-${emp.id}`}>
                            Yeni
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{emp.profession || '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Briefcase className="h-3 w-3 text-gray-400" />
                        {emp.position || emp.position_name || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      {emp.phone ? (
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3 text-gray-400" />
                          {emp.phone}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {emp.start_date || emp.hire_date ? (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-gray-400" />
                          {new Date(emp.start_date || emp.hire_date).toLocaleDateString('tr-TR')}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="secondary" 
                        className={emp.employment_status === 'terminated'
                          ? 'bg-red-100 text-red-700' 
                          : 'bg-green-100 text-green-700'
                        }
                      >
                        {emp.employment_status === 'terminated' ? 'Ayrılmış' : 'Aktif'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {sortedEmployees.length === 0 && employees.length > 0 && (
        <div className="text-center py-8 text-gray-500">
          <Search className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>Aramayla eşleşen çalışan bulunamadı</p>
        </div>
      )}
    </div>
  );
};

export default BranchEmployees;
